import React, { useState } from 'react';
import { Box, Collapse, IconButton, Paper, Stack, Tooltip, Typography } from '@mui/material';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CloseIcon from '@mui/icons-material/Close';
import TextFieldsIcon from '@mui/icons-material/TextFields';
import TransformIcon from '@mui/icons-material/Transform';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import NumbersIcon from '@mui/icons-material/Numbers';
import type { PipelineSourceField, PipelineStep, StepConfig } from './types';
import { FUNCTION_REGISTRY_MAP } from './functionRegistry';
import StepConfigPanel from './StepConfigPanel';

const CATEGORY_ICON: Record<string, React.ReactNode> = {
  text: <TextFieldsIcon sx={{ fontSize: 16 }} />,
  logic: <FilterAltIcon sx={{ fontSize: 16 }} />,
  date: <CalendarMonthIcon sx={{ fontSize: 16 }} />,
  numeric: <NumbersIcon sx={{ fontSize: 16 }} />,
};

const CATEGORY_COLOR: Record<string, string> = {
  text: '#3b82f6',
  logic: '#f59e0b',
  date: '#2563eb',
  numeric: '#10b981',
};

const getStepSummary = (step: PipelineStep): string => {
  const c = step.config;
  switch (step.functionName) {
    case 'UPPER':
      return 'Convert to uppercase';
    case 'LOWER':
      return 'Convert to lowercase';
    case 'TRIM':
      return 'Remove whitespace';
    case 'ABS':
      return 'Absolute value';
    case 'LENGTH':
      return 'Get text length';
    case 'ROUND':
      return `Round to ${c.decimals ?? 2} places`;
    case 'CAST':
      return `Convert to ${c.targetType ?? 'INT'}`;
    case 'SPLIT':
      return `Split by "${c.delimiter ?? ','}" [${c.index ?? 0}]`;
    case 'REPLACE':
      return c.find ? `Replace "${c.find}" with "${c.replaceWith ?? ''}"` : 'Find & Replace';
    case 'CONCAT':
      return `Join ${(c.parts ?? []).length} values`;
    case 'COALESCE':
      return `First non-empty of ${(c.fallbacks ?? []).length} values`;
    case 'NVL':
      return 'Default if empty';
    case 'IF':
      return 'Conditional logic';
    case 'DATE_FORMAT':
      return `Format as ${c.format ?? '%Y-%m-%d'}`;
    case 'PARSE_DATE':
      return `Parse with ${c.format ?? '%Y-%m-%d'}`;
    case 'TO_DATE':
      return c.format ? `Convert to date (${c.format})` : 'Convert to date';
    case 'TO_CHAR':
      return c.format ? `To text (${c.format})` : 'Convert to text';
    case 'TO_TIMESTAMP':
      return c.format ? `To timestamp (${c.format})` : 'Convert to timestamp';
    case 'IS_NULL':
      return 'Check if empty';
    case 'IS_NOT_NULL':
      return 'Check if not empty';
    case 'LITERAL':
      return c.literalValue ? `Constant: "${c.literalValue}"` : 'Constant value';
    case 'SUBSTRING':
      return `Chars ${c.start ?? 1} to ${(c.start ?? 1) + (c.length ?? 10) - 1}`;
    case 'LEFT':
      return `First ${c.length ?? 5} chars`;
    case 'RIGHT':
      return `Last ${c.length ?? 5} chars`;
    case 'LPAD':
      return `Pad left to ${c.padLength ?? 10} with "${c.padChar ?? '0'}"`;
    case 'RPAD':
      return `Pad right to ${c.padLength ?? 10} with "${c.padChar ?? ' '}"`;
    case 'CASE_WHEN': {
      const count = (c.branches ?? []).length;
      return `${count} condition${count !== 1 ? 's' : ''}${c.elseResult ? ' + default' : ''}`;
    }
    default:
      return step.functionName;
  }
};

interface PipelineStepCardProps {
  step: PipelineStep;
  index: number;
  sourceFields: PipelineSourceField[];
  onConfigChange: (stepId: string, config: Partial<StepConfig>) => void;
  onRemove: (stepId: string) => void;
  /** Render props for drag handle from dnd-kit sortable */
  dragHandleProps?: Record<string, any>;
  isDragging?: boolean;
}

const PipelineStepCard: React.FC<PipelineStepCardProps> = ({
  step,
  index,
  sourceFields,
  onConfigChange,
  onRemove,
  dragHandleProps,
  isDragging,
}) => {
  const [expanded, setExpanded] = useState(false);
  const entry = FUNCTION_REGISTRY_MAP[step.functionName];
  const category = entry?.category ?? 'text';
  const color = CATEGORY_COLOR[category] ?? '#64748b';
  const icon = CATEGORY_ICON[category] ?? <TransformIcon sx={{ fontSize: 16 }} />;
  const needsConfig = entry ? !(['UPPER', 'LOWER', 'TRIM', 'ABS', 'IS_NULL', 'IS_NOT_NULL', 'LENGTH'].includes(step.functionName)) : false;

  return (
    <Paper
      variant="outlined"
      sx={{
        borderRadius: 2,
        borderLeft: `3px solid ${color}`,
        opacity: isDragging ? 0.5 : 1,
        transition: 'box-shadow 0.15s, opacity 0.15s',
        '&:hover': { boxShadow: `0 0 0 1px ${color}33` },
        overflow: 'hidden',
      }}
    >
      {/* Header row */}
      <Stack
        direction="row"
        alignItems="center"
        spacing={0.5}
        sx={{ px: 1, py: 0.5, cursor: needsConfig ? 'pointer' : 'default' }}
        onClick={() => needsConfig && setExpanded(!expanded)}
      >
        <Box
          {...(dragHandleProps ?? {})}
          sx={{ cursor: 'grab', display: 'flex', alignItems: 'center', color: 'text.disabled' }}
        >
          <DragIndicatorIcon sx={{ fontSize: 16 }} />
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', color }}>
          {icon}
        </Box>

        <Typography
          variant="body2"
          sx={{
            fontWeight: 600,
            fontSize: '0.78rem',
            color,
            fontFamily: 'monospace',
            minWidth: 0,
          }}
        >
          {step.functionName}
        </Typography>

        <Typography
          variant="caption"
          color="text.secondary"
          sx={{
            flex: 1,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            fontSize: '0.72rem',
          }}
        >
          {getStepSummary(step)}
        </Typography>

        {needsConfig && (
          <IconButton size="small" sx={{ p: 0.25 }}>
            {expanded ? (
              <ExpandLessIcon sx={{ fontSize: 16 }} />
            ) : (
              <ExpandMoreIcon sx={{ fontSize: 16 }} />
            )}
          </IconButton>
        )}

        <Tooltip title="Remove step">
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onRemove(step.id);
            }}
            sx={{ p: 0.25, color: 'text.disabled', '&:hover': { color: 'error.main' } }}
          >
            <CloseIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </Tooltip>
      </Stack>

      {/* Config panel */}
      {needsConfig && (
        <Collapse in={expanded} unmountOnExit>
          <Box sx={{ px: 1.5, pb: 1, pt: 0.5, borderTop: '1px solid', borderColor: 'divider' }}>
            <StepConfigPanel
              functionName={step.functionName}
              config={step.config}
              sourceFields={sourceFields}
              onChange={(partial) => onConfigChange(step.id, partial)}
            />
          </Box>
        </Collapse>
      )}
    </Paper>
  );
};

export default PipelineStepCard;
